import React, { useState } from 'react';
import './ExportModal.css';
import { saveAs } from 'file-saver';

const ExportModal = ({ 
  isOpen, 
  onClose, 
  data, 
  filename = 'export', 
  title = 'Export Data',
  columns = [],
  customDataProcessor = null,
  reportType = null,
  onReportTypeChange = null,
  reportTypeOptions = []
}) => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [isExporting, setIsExporting] = useState(false);

  // Default data processor - converts object array to 2D array
  const defaultDataProcessor = (data, columns) => {
    if (!data || !Array.isArray(data)) return [];
    
    const processedData = [];
    
    // Add headers if columns are provided
    if (columns && columns.length > 0) {
      processedData.push(columns);
    }
    
    // Process data rows
    data.forEach(item => {
      if (columns && columns.length > 0) {
        // Use specified columns
        const row = columns.map(col => {
          if (typeof item === 'object' && item !== null) {
            return item[col] || '';
          }
          return item;
        });
        processedData.push(row);
      } else {
        // Use all object properties or the item itself
        if (typeof item === 'object' && item !== null) {
          processedData.push(Object.values(item));
        } else {
          processedData.push([item]);
        }
      }
    });
    
    return processedData;
  };

  // Process data using custom processor or default
  const getProcessedData = async () => {
    if (customDataProcessor) {
      return await customDataProcessor(data, selectedColumns);
    }
    return defaultDataProcessor(data, selectedColumns);
  };

  // Export to CSV
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const processedData = await getProcessedData();
      
      // If custom processor returns empty array, it handled the download itself
      if (customDataProcessor && (!processedData || processedData.length === 0)) {
        onClose();
        return;
      }
      
      const csvContent = processedData.map(row => 
        row.map(cell => `"${cell || ''}"`).join(',')
      ).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const timestamp = new Date().toISOString().split('T')[0];
      const csvFilename = `${filename}_${timestamp}.csv`;
      
      saveAs(blob, csvFilename);
      onClose();
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export to Excel — the writer is ExcelJS behind a lazy import (the Long Sheet precedent,
  // plans/completed/timesheet-field-ticket-quality/frontend-xlsx-removal.md): the modal's initial load carries no spreadsheet engine,
  // and the auto-fit widths + styled header ride in the shared aoaWorkbook module.
  const exportToXLSX = async () => {
    setIsExporting(true);
    try {
      const processedData = await getProcessedData();

      // If custom processor returns empty array, it handled the download itself
      if (customDataProcessor && (!processedData || processedData.length === 0)) {
        onClose();
        return;
      }

      const { downloadAoaWorkbook } = await import('./aoaWorkbook');
      const timestamp = new Date().toISOString().split('T')[0];
      await downloadAoaWorkbook(
        [{ name: 'Export Data', rows: processedData, widths: 'auto', styledHeader: true }],
        `${filename}_${timestamp}.xlsx`
      );
      onClose();
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle column selection
  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => 
      prev.includes(column) 
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  // Select/Deselect all columns
  const toggleAllColumns = () => {
    setSelectedColumns(prev => 
      prev.length === columns.length ? [] : [...columns]
    );
  };

  // Handle format selection and export
  const handleExport = (format) => {
    setSelectedFormat(format);
    if (format === 'csv') {
      exportToCSV();
    } else if (format === 'xlsx') {
      exportToXLSX();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3>{title}</h3>
          <button 
            className="export-modal-close"
            onClick={onClose}
            disabled={isExporting}
          >
            ×
          </button>
        </div>
        
        <div className="export-modal-body">
          {/* Report Type Selection */}
          {reportTypeOptions && reportTypeOptions.length > 0 && (
            <div className="export-report-type-section">
              <h4>Select Report Type</h4>
              <div className="export-report-type-options">
                {reportTypeOptions.map((option, index) => (
                  <label key={index} className="export-report-type-option">
                    <input
                      type="radio"
                      name="reportType"
                      value={option.value}
                      checked={reportType === option.value}
                      onChange={() => onReportTypeChange && onReportTypeChange(option.value)}
                      disabled={isExporting}
                    />
                    <span className="export-report-type-label">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Column Selection */}
          {columns && columns.length > 0 && (
            <div className="export-column-section">
              <div className="export-section-header">
                <h4>Select Columns to Export</h4>
                <button 
                  className="export-toggle-all-btn"
                  onClick={toggleAllColumns}
                  disabled={isExporting}
                >
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="export-columns-grid">
                {columns.map((column, index) => (
                  <label key={index} className="export-column-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(column)}
                      onChange={() => handleColumnToggle(column)}
                      disabled={isExporting}
                    />
                    <span className="export-column-label">{column}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Format Selection */}
          <div className="export-format-section">
            <h4>Choose Export Format</h4>
            <div className="export-format-options">
              <button 
                className={`export-format-btn csv-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting || (columns.length > 0 && selectedColumns.length === 0)}
              >
                <span className="export-icon">📊</span>
                <div className="export-format-info">
                  <div className="export-format-title">CSV</div>
                  <div className="export-format-desc">Comma-separated values for data analysis</div>
                </div>
              </button>
              
              <button 
                className={`export-format-btn xlsx-btn ${selectedFormat === 'xlsx' ? 'selected' : ''}`}
                onClick={() => handleExport('xlsx')}
                disabled={isExporting || (columns.length > 0 && selectedColumns.length === 0)}
              >
                <span className="export-icon">📈</span>
                <div className="export-format-info">
                  <div className="export-format-title">XLSX</div>
                  <div className="export-format-desc">Excel format with formatting and styling</div>
                </div>
              </button>
            </div>
          </div>

          {/* Export Status */}
          {isExporting && (
            <div className="export-status">
              <div className="export-loading">
                <div className="export-spinner"></div>
                <span>Exporting data...</span>
              </div>
            </div>
          )}

          {/* Warning for no columns selected */}
          {columns.length > 0 && selectedColumns.length === 0 && (
            <div className="export-warning">
              <span>⚠️ Please select at least one column to export</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
